/**
 * PostcardCanvas
 * is_full_bleed = false → 吉祥物版（暖色漸層 + 右側框）
 * is_full_bleed = true  → 風景版（圖片佔右側 3/5 + 左側背景圖）
 */
import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

// ── 自訂下拉 (桌面版 canvas 內) ─────────────────────────────────────
function FromSelect({ value, options, onChange, boxStyle, labelStyle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selected = options.find(o => o.value === value) ?? options[0];

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ ...boxStyle, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected?.label ?? value}
        </span>
        <svg viewBox="0 0 10 6" style={{ width: 'clamp(7px, 0.9cqw, 14px)', height: 'clamp(4px, 0.55cqw, 9px)', flexShrink: 0, marginLeft: 4, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', fill: 'none', stroke: '#9E6D61', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
          <path d="M1 1l4 4 4-4" />
        </svg>
      </div>
      {open && (
        <div style={{ position: 'absolute', bottom: '100%', left: 0, width: '100%', background: '#FFFDF9', border: '1px solid #E8CFC0', borderRadius: 'clamp(4px, 0.55cqw, 10px)', boxShadow: '0 4px 20px rgba(62,36,24,0.12)', overflow: 'hidden', zIndex: 50, marginBottom: 4 }}>
          {options.map(o => {
            const isActive = o.value === value;
            return (
              <div key={o.value} onMouseDown={e => { e.preventDefault(); onChange(o.value); setOpen(false); }}
                style={{ padding: 'clamp(5px, 0.7cqw, 12px) clamp(7px, 1.1cqw, 18px)', fontSize: labelStyle.fontSize, fontFamily: 'inherit', fontWeight: isActive ? 500 : 300, color: isActive ? '#5A2A23' : '#6E4C3E', background: isActive ? 'rgba(198,125,93,0.08)' : 'transparent', cursor: 'pointer', borderBottom: '1px solid #F5E8DF', display: 'flex', alignItems: 'center', gap: 6 }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(198,125,93,0.04)'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                {isActive
                  ? <svg viewBox="0 0 10 8" style={{ width: 'clamp(6px, 0.8cqw, 12px)', flexShrink: 0, fill: 'none', stroke: '#C67D5D', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }}><path d="M1 4l3 3 5-6" /></svg>
                  : <span style={{ width: 'clamp(6px, 0.8cqw, 12px)', flexShrink: 0 }} />}
                {o.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── 自訂下拉 (手機版 canvas 內) ─────────────────────────────────────
function MobileFromSelect({ value, options, onChange, fs, lh, pad, br }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selected = options.find(o => o.value === value) ?? options[0];
  const boxStyle = {
    width: '100%', boxSizing: 'border-box', background: 'white', border: '1px solid #E8CFC0',
    borderRadius: br, padding: pad, fontSize: fs, fontFamily: 'inherit', fontWeight: 300,
    color: '#3E2418', lineHeight: lh, height: `calc(${lh} + clamp(14px, 3.5vw, 28px))`,
    boxShadow: '1px 2px 12px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none',
    whiteSpace: 'nowrap', overflow: 'hidden', marginTop: 'clamp(3px, 0.8vw, 8px)',
  };
  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <div onClick={() => setOpen(o => !o)} style={boxStyle}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{selected?.label ?? value}</span>
        <svg viewBox="0 0 10 6" style={{ width: 'clamp(8px, 2vw, 13px)', height: 'clamp(5px, 1.3vw, 8px)', flexShrink: 0, marginLeft: 4, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', fill: 'none', stroke: '#9E6D61', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
          <path d="M1 1l4 4 4-4" />
        </svg>
      </div>
      {open && (
        <div style={{ position: 'absolute', bottom: '100%', left: 0, width: '100%', background: '#FFFDF9', border: '1px solid #E8CFC0', borderRadius: br, boxShadow: '0 4px 20px rgba(62,36,24,0.15)', overflow: 'hidden', zIndex: 50, marginBottom: 4 }}>
          {options.map(o => {
            const isActive = o.value === value;
            return (
              <div key={o.value} onMouseDown={e => { e.preventDefault(); onChange(o.value); setOpen(false); }}
                style={{ padding: `clamp(8px, 2vw, 14px) clamp(10px, 2.5vw, 18px)`, fontSize: fs, fontFamily: 'inherit', fontWeight: isActive ? 500 : 300, color: isActive ? '#5A2A23' : '#6E4C3E', background: isActive ? 'rgba(198,125,93,0.08)' : 'transparent', cursor: 'pointer', borderBottom: '1px solid #F5E8DF', display: 'flex', alignItems: 'center', gap: 8 }}
              >
                {isActive
                  ? <svg viewBox="0 0 10 8" style={{ width: 10, flexShrink: 0, fill: 'none', stroke: '#C67D5D', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }}><path d="M1 4l3 3 5-6" /></svg>
                  : <span style={{ width: 10, flexShrink: 0 }} />}
                {o.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── 共用欄位樣式 helper ───────────────────────────────────────────────
function makeDesktopStyles() {
  const fs = 'clamp(9px, 1.15cqw, 20px)';
  const lh = 'clamp(22px, 2.8cqw, 50px)';
  const pad = 'clamp(5px, 0.75cqw, 14px) clamp(7px, 1.1cqw, 20px)';
  const br = 'clamp(4px, 0.55cqw, 10px)';
  const boxBase = {
    display: 'block', width: '100%', boxSizing: 'border-box',
    boxShadow: '1px 2px 12px rgba(0,0,0,0.06)', borderRadius: br,
    border: '1px solid #E8CFC0', outline: 'none', fontFamily: 'inherit',
    fontWeight: 300, fontSize: fs, color: '#3E2418', lineHeight: lh,
    marginTop: 'clamp(3px, 0.4cqw, 7px)',
  };
  const singleBox = { ...boxBase, background: 'white', padding: pad, height: `calc(${lh} + clamp(10px, 1.5cqw, 28px))`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', borderLeft: '3px solid #C67D5D' };
  const multiBox = {
    ...boxBase, background: 'white', padding: pad, resize: 'none', overflow: 'hidden', borderLeft: '3px solid #C67D5D',
  };
  const labelSt = { color: '#9E6D61', fontSize: 'clamp(8px, 1.35cqw, 24px)', fontWeight: 500, lineHeight: 1.5, paddingTop: 'clamp(5px, 0.75cqw, 12px)' };
  const dividerSt = { width: '100%', height: 1, background: '#F1E2D7', marginTop: 'clamp(6px, 0.9cqw, 16px)' };
  return { fs, lh, pad, br, singleBox, multiBox, labelSt, dividerSt };
}

function makeMobileStyles() {
  const fs = 'clamp(13px, 3.8vw, 18px)';
  const lh = 'clamp(26px, 6.5vw, 38px)';
  const fsl = 'clamp(11px, 2.8vw, 14px)';
  const pad = 'clamp(8px, 2.2vw, 14px) clamp(12px, 3.2vw, 20px)';
  const br = 'clamp(7px, 2vw, 12px)';
  const fieldBox = {
    width: '100%', boxSizing: 'border-box', background: 'white', border: '1px solid #E8CFC0',
    borderRadius: br, padding: pad, fontSize: fs, fontFamily: 'inherit', fontWeight: 300,
    color: '#3E2418', lineHeight: lh, boxShadow: '1px 2px 12px rgba(0,0,0,0.06)',
    outline: 'none', marginTop: 'clamp(3px, 0.8vw, 8px)',
  };
  const singleBox = { ...fieldBox, height: `calc(${lh} + clamp(14px, 3.5vw, 28px))`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', borderLeft: '3px solid #C67D5D' };
  const multiBox = {
    ...fieldBox, resize: 'none', overflow: 'hidden', borderLeft: '3px solid #C67D5D',
  };
  const labelSt = { color: '#9E6D61', fontSize: fsl, fontFamily: 'inherit', fontWeight: 500, lineHeight: 1.5, marginTop: 'clamp(10px, 2.5vw, 18px)' };
  const dividerSt = { width: '87.5%', height: 1, background: '#F1E2D7', margin: 'clamp(10px, 2.5vw, 18px) auto 0' };
  return { fs, lh, fsl, pad, br, singleBox, multiBox, labelSt, dividerSt };
}

// ── Main Component ────────────────────────────────────────────────────
export default function PostcardCanvas({
  item,
  fromName, toName, content,
  isMobile,
  editable = false,
  onToName, onContent,
  fromOptions = [],
  onFromName,
  maxContent = 80,
  maxName = 30,
  forwardRef,
}) {
  const bgUrl = isMobile ? item?.mobile_url : item?.desktop_url;
  const isFullBleed = item?.is_full_bleed ?? false;

  // ════════════════════════════════════════════════════════════════════
  // 手機版
  // ════════════════════════════════════════════════════════════════════
  if (isMobile) {
    const { fs, lh, pad, br, singleBox, multiBox, labelSt, dividerSt } = makeMobileStyles();
    const showDropdown = editable && fromOptions.length > 1;
    const fieldPad = 'clamp(20px, 5vw, 36px)';

    // ── 手機 is_full_bleed = false（吉祥物） ──────────────────────────
    const readSingleM = {
      ...singleBox,
      borderLeft: '1px solid #E8CFC0',
      boxShadow: 'none',
      color: '#3E2418',
      display: 'flex',
      alignItems: 'center',
      ...(!editable ? { background: 'rgba(255,255,255,0.5)' } : {}),
    };
    const readMultiM = {
      ...multiBox,
      borderLeft: '1px solid #E8CFC0',
      boxShadow: 'none',
      color: '#3E2418',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      minHeight: `calc(5 * ${lh} + clamp(16px, 4.4vw, 28px))`,
      ...(!editable ? { background: 'rgba(255,255,255,0.5)' } : {}),
    };
    const readLabelM = { ...labelSt };

    if (!isFullBleed) {
      return (
        <div ref={forwardRef} style={{ width: '100%', background: 'linear-gradient(180deg, #ffffff 0%, #F7EAD3 100%)', overflow: 'hidden', position: 'relative', borderRadius: 'clamp(12px, 3vw, 20px)' }}>
          {/* 裝飾圓 左上 */}
          <div style={{ position: 'absolute', width: '75%', height: 0, paddingBottom: '75%', left: '-28%', top: '-11.5%', background: 'rgba(243,214,169,0.33)', borderRadius: '50%', pointerEvents: 'none' }} />
          {/* 圖片 */}
          <div style={{ padding: `clamp(14px, 3.5vw, 24px) ${fieldPad}`, position: 'relative' }}>
              <div style={{ position: 'relative', width: '100%', aspectRatio: '69/44', borderRadius: 15, overflow: 'hidden', background: '#EAD4AF', boxShadow: '0px 4px 10px rgba(0,0,0,0.40)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {bgUrl && <img src={bgUrl} alt={item?.name ?? '明信片'} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />}
            </div>
          </div>
          {/* 欄位 */}
          <div style={{ padding: `0 ${fieldPad} clamp(20px, 5vw, 36px)` }}>
            <div style={editable ? labelSt : readLabelM}>To ─ 收件者</div>
            {editable ? <>
              <input value={toName ?? ''} onChange={e => onToName?.(e.target.value)} placeholder="收件者姓名" maxLength={maxName} style={singleBox} />
              <div style={{ textAlign: 'right', fontSize: 'clamp(12px, 2.2vw, 16px)', color: (toName ?? '').length > maxName * 0.8 ? '#E20020' : '#544640', marginTop: 2 }}>{(toName ?? '').length}/{maxName}</div>
            </>
              : <div style={readSingleM}>{toName || '收件者姓名'}</div>}
                  <div style={editable ? labelSt : readLabelM}>Message ─ 祝福內容</div>
            {editable ? <>
              <textarea value={content ?? ''} onChange={e => onContent?.(e.target.value)} placeholder="在此寫下您的祝福⋯" maxLength={maxContent} rows={5} style={multiBox} />
              <div style={{ textAlign: 'right', fontSize: 'clamp(12px, 2.2vw, 16px)', color: (content ?? '').length > maxContent * 0.8 ? '#E20020' : '#544640', marginTop: 2 }}>{(content ?? '').length}/{maxContent}</div>
            </> : <div style={readMultiM}>{content || '在此寫下您的祝福⋯'}</div>}
                  <div style={editable ? labelSt : readLabelM}>From ─ 寄件者</div>
            {showDropdown
              ? <MobileFromSelect value={fromName ?? ''} options={fromOptions} onChange={onFromName} fs={fs} lh={lh} pad={pad} br={br} />
              : <div style={readSingleM}>{fromName || '您的姓名'}</div>}
          </div>
        </div>
      );
    }

    // ── 手機 is_full_bleed = true（風景） ────────────────────────────
    return (
      <div ref={forwardRef} style={{ width: '100%', overflow: 'hidden', position: 'relative', background: '#fff', borderRadius: 'clamp(12px, 3vw, 20px)' }}>
        {/* 上半：api 圖片佔正方形 */}
        <div style={{ width: '100%', aspectRatio: '1/1', overflow: 'hidden', position: 'relative' }}>
          {bgUrl && <img src={bgUrl} alt={item?.name ?? '明信片'} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
        </div>
        {/* 下半：欄位區 白底 */}
        <div style={{ background: '#fff', padding: `clamp(14px, 3.5vw, 22px) ${fieldPad} clamp(20px, 5vw, 36px)` }}>
          <div style={editable ? labelSt : readLabelM}>To ─ 收件者</div>
          {editable ? <>
            <input value={toName ?? ''} onChange={e => onToName?.(e.target.value)} placeholder="收件者姓名" maxLength={maxName} style={singleBox} />
            <div style={{ textAlign: 'right', fontSize: 'clamp(12px, 2.2vw, 16px)', color: (toName ?? '').length > maxName * 0.8 ? '#E20020' : '#544640', marginTop: 2 }}>{(toName ?? '').length}/{maxName}</div>
          </>
            : <div style={readSingleM}>{toName || '收件者姓名'}</div>}
              <div style={editable ? labelSt : readLabelM}>Message ─ 祝福內容</div>
          {editable ? <>
            <textarea value={content ?? ''} onChange={e => onContent?.(e.target.value)} placeholder="在此寫下您的祝福⋯" maxLength={maxContent} rows={5} style={multiBox} />
            <div style={{ textAlign: 'right', fontSize: 'clamp(12px, 2.2vw, 16px)', color: (content ?? '').length > maxContent * 0.8 ? '#E20020' : '#544640', marginTop: 2 }}>{(content ?? '').length}/{maxContent}</div>
          </> : <div style={readMultiM}>{content || '在此寫下您的祝福⋯'}</div>}
              <div style={editable ? labelSt : readLabelM}>From ─ 寄件者</div>
          {showDropdown
            ? <MobileFromSelect value={fromName ?? ''} options={fromOptions} onChange={onFromName} fs={fs} lh={lh} pad={pad} br={br} />
            : <div style={readSingleM}>{fromName || '您的姓名'}</div>}
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════
  // 桌面版
  // ════════════════════════════════════════════════════════════════════
  const { fs, lh, singleBox, multiBox, labelSt, dividerSt } = makeDesktopStyles();
  const showDropdown = editable && fromOptions.length > 1;

  // ── 桌面 is_full_bleed = false（吉祥物） ────────────────────────────
  if (!isFullBleed) {
    return (
      <div ref={forwardRef} className="relative w-full overflow-hidden" style={{ aspectRatio: '16/9', borderRadius: 18, boxShadow: '0 4px 32px rgba(0,0,0,0.18)', background: 'linear-gradient(180deg, #ffffff 0%, #F7EAD3 100%)', containerType: 'inline-size' }}>
        {/* 裝飾圓 左上 */}
        <div style={{ position: 'absolute', width: '29%', height: '51%', left: '-6%', top: '-20%', background: 'rgba(243,214,169,0.33)', borderRadius: '50%', pointerEvents: 'none' }} />
        {/* 裝飾圓 右上 */}
        <div style={{ position: 'absolute', width: '17%', height: '31%', right: 0, top: '8%', background: 'rgba(241,222,192,0.33)', borderRadius: '50%', pointerEvents: 'none' }} />
        {/* 右側圖片 */}
        <div style={{ position: 'absolute', right: '4%', top: '50%', transform: 'translateY(-50%)', width: '64%', aspectRatio: '600/383', borderRadius: '3.5%', overflow: 'hidden', background: '#EAD4AF' }}>
          {bgUrl && <img src={bgUrl} alt={item?.name ?? '明信片'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
        </div>
        {/* 左側欄位 */}
        <DesktopFields editable={editable} toName={toName} content={content} fromName={fromName} fromOptions={fromOptions} onToName={onToName} onContent={onContent} onFromName={onFromName} maxContent={maxContent} maxName={maxName} showDropdown={showDropdown} singleBox={singleBox} multiBox={multiBox} labelSt={labelSt} dividerSt={dividerSt} />
      </div>
    );
  }

  // ── 桌面 is_full_bleed = true（風景） ──────────────────────────────
  return (
    <div ref={forwardRef} className="relative w-full" style={{ aspectRatio: '16/9', borderRadius: 18, boxShadow: '0 4px 32px rgba(0,0,0,0.18)', overflow: 'hidden', containerType: 'inline-size', background: '#fff' }}>
      {/* 右側：api 圖片佔右 3/5 全高 */}
      <div style={{ position: 'absolute', left: '42%', top: 0, width: '60%', height: '100%', overflow: 'hidden' }}>
        {bgUrl && <img src={bgUrl} alt={item?.name ?? '明信片'} style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center' }} />}
      </div>
      {/* 左側：背景圖 */}
      <div style={{ position: 'absolute', left: 0, top: 0, width: '44%', height: '100%', overflow: 'hidden' }}>
        <img src="/image/postcard/bg1.jpg" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
      </div>
      {/* 藍色漸層 疊在左側 */}
      <div style={{ position: 'absolute', left: 0, top: 0, width: '55%', height: '35%', background: 'linear-gradient(90deg, rgba(169,207,243,0.48) 0%, rgba(169,207,243,0) 100%)', pointerEvents: 'none' }} />
      {/* 左側欄位（寬度稍大） */}
      <DesktopFields editable={editable} toName={toName} content={content} fromName={fromName} fromOptions={fromOptions} onToName={onToName} onContent={onContent} onFromName={onFromName} maxContent={maxContent} maxName={maxName} showDropdown={showDropdown} singleBox={singleBox} multiBox={multiBox} labelSt={labelSt} dividerSt={dividerSt} left="4.8%" width="33%" />
    </div>
  );
}

// ── 桌面版欄位區（共用） ─────────────────────────────────────────────
function DesktopFields({ editable, toName, content, fromName, fromOptions, onToName, onContent, onFromName, maxContent, maxName, showDropdown, singleBox, multiBox, labelSt, dividerSt, left = '4%', width = '27%' }) {
  const { lh } = makeDesktopStyles();
  const readSingle = {
    ...singleBox,
    background: 'white',
    borderLeft: '1px solid #E8CFC0',
    boxShadow: 'none',
    color: '#3E2418',
    ...(!editable ? { background: 'rgba(255,255,255,0.5)' } : {}),
  };
  const readMulti = {
    ...multiBox,
    background: 'white',
    borderLeft: '1px solid #E8CFC0',
    boxShadow: 'none',
    color: '#3E2418',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    minHeight: `calc(4 * ${lh} + clamp(10px, 1.5cqw, 28px))`,
    ...(!editable ? { background: 'rgba(255,255,255,0.5)' } : {}),
  };
  const readLabel = { ...labelSt };

  return (
    <div style={{ position: 'absolute', left, top: '10%', width, display: 'flex', flexDirection: 'column' }}>
      <div style={editable ? labelSt : readLabel}>To ─ 收件者</div>
      {editable
        ? <div>
          <input value={toName ?? ''} onChange={e => onToName?.(e.target.value)} placeholder="收件者姓名" maxLength={maxName} style={singleBox} />
          <div style={{ textAlign: 'right', fontSize: 'clamp(10px, 0.85cqw, 12px)', color: (toName ?? '').length > maxName * 0.8 ? '#E20020' : '#544640', marginTop: 2 }}>{(toName ?? '').length}/{maxName}</div>
        </div>
        : <div style={readSingle}>{toName || '收件者姓名'}</div>}
      <div style={editable ? labelSt : readLabel}>Message ─ 祝福內容</div>
      {editable
        ? <div>
          <textarea value={content ?? ''} onChange={e => onContent?.(e.target.value)} placeholder="在此寫下您的祝福⋯" maxLength={maxContent} rows={4} style={multiBox} />
          <div style={{ textAlign: 'right', fontSize: 'clamp(10px, 0.85cqw, 12px)', color: (content ?? '').length > maxContent * 0.8 ? '#E20020' : '#544640', marginTop: 2 }}>{(content ?? '').length}/{maxContent}</div>
        </div>
        : <div style={readMulti}>{content || '在此寫下您的祝福⋯'}</div>}
      <div style={editable ? labelSt : readLabel}>From ─ 寄件者</div>
      {showDropdown
        ? <FromSelect value={fromName ?? ''} options={fromOptions} onChange={onFromName} boxStyle={singleBox} labelStyle={labelSt} />
        : <div style={editable ? { ...singleBox, color: fromName ? '#3E2418' : '#C5A596' } : readSingle}>{fromName || '您的姓名'}</div>}
    </div>
  );
}

PostcardCanvas.propTypes = {
  item: PropTypes.shape({ item_id: PropTypes.number, name: PropTypes.string, desktop_url: PropTypes.string, mobile_url: PropTypes.string, is_full_bleed: PropTypes.bool }),
  fromName: PropTypes.string,
  toName: PropTypes.string,
  content: PropTypes.string,
  isMobile: PropTypes.bool,
  editable: PropTypes.bool,
  onToName: PropTypes.func,
  onContent: PropTypes.func,
  fromOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
  onFromName: PropTypes.func,
  maxContent: PropTypes.number,
  maxName: PropTypes.number,
  forwardRef: PropTypes.object,
};
